import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { apiAuth } from "@/api/auth";

export default function ForgotPasswordPage() {
  const [sp] = useSearchParams();
  const [identifier, setIdentifier] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [sentRole, setSentRole] = useState("");

  useEffect(() => { document.title = "รีเซ็ตรหัสผ่าน • Kuizy"; }, []);

  const prefer = (() => {
    const t = (sp.get("type") || "").toLowerCase();
    return t === "learner" ? "learner" : t === "admin" ? "admin" : "auto";
  })();

  const tryAdmin = async id => {
    return apiAuth.post("/forgot-password", { email: id });
  };
  const tryLearner = async id => {
    return apiAuth.post("/app-forgot-password", { query: id });
  };

  const submit = async e => {
    e.preventDefault();
    if (saving) return;
    const id = identifier.trim();
    if (!id) return;
    setError("");
    setSaving(true);
    try {
      if (prefer === "learner") {
        try {
          await tryLearner(id);
          setSentRole("learner");
          setSent(true);
          return;
        } catch (e1) {
          const code = e1?.response?.data?.error || e1?.response?.data?.detail || e1?.response?.data?.message || "";
          if (String(code).toLowerCase() !== "user_not_found" && e1?.response?.status !== 404) throw e1;
        }
        await tryAdmin(id);
        setSentRole("admin");
        setSent(true);
        return;
      }
      if (prefer === "admin") {
        try {
          await tryAdmin(id);
          setSentRole("admin");
          setSent(true);
          return;
        } catch (e1) {
          const code = e1?.response?.data?.error || e1?.response?.data?.detail || e1?.response?.data?.message || "";
          if (String(code).toLowerCase() !== "user_not_found" && e1?.response?.status !== 404) throw e1;
        }
        await tryLearner(id);
        setSentRole("learner");
        setSent(true);
        return;
      }
      try {
        await tryAdmin(id);
        setSentRole("admin");
        setSent(true);
      } catch (e1) {
        const code = e1?.response?.data?.error || e1?.response?.data?.detail || e1?.response?.data?.message || "";
        if (String(code).toLowerCase() !== "user_not_found" && e1?.response?.status !== 404) {
          throw e1;
        }
        await tryLearner(id);
        setSentRole("learner");
        setSent(true);
      }
    } catch (err) {
      const payload = err?.response?.data || {};
      const code = payload.error || payload.detail || payload.message || "unknown_error";
      setError(
        code === "user_not_found"
          ? "ไม่พบผู้ใช้นี้ กรุณาตรวจสอบอีกครั้ง"
          : code === "unauthorized"
            ? "คุณไม่ได้รับอนุญาตให้ทำรายการนี้"
            : "เกิดข้อผิดพลาด กรุณาลองใหม่ภายหลัง"
      );
    } finally {
      setSaving(false);
    }
  };

  const fieldCls = "w-full border border-gray-300 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-primary bg-white/70";

  return (
    <section className="min-h-screen flex items-center justify-center bg-gradient-to-tr from-primary via-info to-secondary p-4">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="w-full max-w-md bg-white/80 backdrop-blur-xl rounded-2xl px-8 py-10 shadow-2xl flex flex-col gap-6"
      >
        {sent ? (
          <>
            <h1 className="text-3xl font-extrabold text-center text-primary">โปรดตรวจสอบอีเมล</h1>
            <p className="text-center text-gray-600 -mt-2">
              เราส่งลิงก์ตั้งรหัสผ่านใหม่ไปที่
            </p>
            <p className="text-center text-gray-600 -mt-2"><b>{identifier}</b>{sentRole ? ` (${sentRole === "admin" ? "ผู้ดูแลระบบ" : "ผู้เรียน"})` : ""}</p>
            <Link to="/login" className="block mt-6 text-center text-primary font-semibold underline">กลับหน้าเข้าสู่ระบบ</Link>
          </>
        ) : (
          <>
            <div className="space-y-1 text-center">
              <h1 className="text-3xl font-extrabold text-primary">
                <img src="/images/app-logo.png" className="h-20 mx-auto opacity-95 mb-5" />
              </h1>
              <h1 className="text-2xl font-extrabold text-gray-900">ลืมรหัสผ่าน</h1>
              <p className="text-gray-500 text-sm">กรุณากรอกอีเมลหรือชื่อผู้ใช้</p>
            </div>

            {error ? <p className="text-center text-sm text-danger font-medium -mt-2">{error}</p> : null}

            <form onSubmit={submit} className="flex flex-col gap-4">
              <input
                placeholder="อีเมลหรือชื่อผู้ใช้"
                value={identifier}
                onChange={e => setIdentifier(e.target.value)}
                className={fieldCls}
                disabled={saving}
              />
              <button
                disabled={saving || !identifier.trim()}
                className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 disabled:opacity-50 transition text-white font-semibold rounded-xl py-3 flex items-center justify-center gap-2 shadow-lg"
              >
                {saving && <Loader2 className="h-5 w-5 animate-spin" />}
                {saving ? "กำลังส่ง…" : "ส่งลิงก์"}
              </button>
              <p className="text-center text-sm">
                มีบัญชีอยู่แล้ว?{" "}
                <Link to="/login" className="text-primary font-semibold">เข้าสู่ระบบ</Link>
              </p>
            </form>
          </>
        )}
      </motion.div>
    </section>
  );
}
